import React, { Fragment, useState } from "react";
import { IconButton, Menu, MenuItem, Box, Divider, Typography, Stack, Tooltip } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MessageIcon from "@mui/icons-material/Message";
import CustomizedSwitches from "./CustomSwitch";

const ActionMenu = ({ actions = [], row, onStatusToggle, showStatusToggle = true, statusField = "is_active" }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action) => {
        handleClose();
        if (action.onClick) {
            action.onClick(row);
        }
    };

    const handleStatusToggle = () => {
        handleClose();
        if (onStatusToggle) {
            onStatusToggle(row);
        }
    };

    const isActive = row?.[statusField];

    return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Tooltip title="Actions" placement="top">
                <IconButton
                    aria-label="actions"
                    aria-controls="action-menu"
                    aria-haspopup="true"
                    onClick={handleMenuClick}
                    size="small"
                    sx={{
                        color: "#666",
                        "&:hover": {
                            backgroundColor: "#f5f5f5",
                            color: "#1976d2",
                        },
                        transition: "all 0.2s ease-in-out",
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu
                id="action-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                PaperProps={{
                    style: {
                        minWidth: 220,
                        paddingTop: 8,
                        paddingBottom: 8,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        borderRadius: 12,
                        border: "1px solid #e0e0e0",
                    },
                }}
                MenuListProps={{
                    style: {
                        padding: 0,
                    },
                }}
            >
                {/* Action Items */}
                {actions.map((action, index) => (
                    <Fragment key={index}>
                        <MenuItem
                            onClick={() => handleAction(action)}
                            sx={{
                                minHeight: 48,
                                px: 3,
                                py: 1.5,
                                mx: 1,
                                borderRadius: 1,
                                "&:hover": {
                                    backgroundColor: action.hoverColor || "#f8f9fa",
                                    transform: "translateX(2px)",
                                },
                                transition: "all 0.2s ease-in-out",
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
                                {action.icon && (
                                    <Box
                                        sx={{
                                            color: action.color || "#666",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 20,
                                            height: 20,
                                        }}
                                    >
                                        {action.icon}
                                    </Box>
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: action.color || "#666",
                                        fontWeight: 500,
                                        flex: 1,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    {action.label}
                                </Typography>
                            </Stack>
                        </MenuItem>
                        {index !== actions.length - 1 && (
                            <Divider
                                sx={{
                                    my: 0.5,
                                    mx: 2,
                                    borderColor: "#f0f0f0",
                                }}
                            />
                        )}
                    </Fragment>
                ))}

                {/* Status Toggle Section */}
                {showStatusToggle && (
                    <>
                        {actions.length > 0 && (
                            <Divider
                                sx={{
                                    my: 0.5,
                                    mx: 2,
                                    borderColor: "#f0f0f0",
                                }}
                            />
                        )}
                        <MenuItem
                            sx={{
                                minHeight: 48,
                                px: 3,
                                py: 1.5,
                                mx: 1,
                                borderRadius: 1,
                                "&:hover": {
                                    backgroundColor: isActive ? "#ffebee" : "#e8f5e8",
                                    transform: "translateX(2px)",
                                },
                                transition: "all 0.2s ease-in-out",
                            }}
                        >
                            <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
                                <Stack direction="row" spacing={2} alignItems="center">
                                    {isActive ? (
                                        <CheckCircleIcon
                                            sx={{
                                                color: "#388e3c",
                                                fontSize: 20,
                                                width: 20,
                                                height: 20,
                                            }}
                                        />
                                    ) : (
                                        <BlockIcon
                                            sx={{
                                                color: "#d32f2f",
                                                fontSize: 20,
                                                width: 20,
                                                height: 20,
                                            }}
                                        />
                                    )}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: isActive ? "#388e3c" : "#d32f2f",
                                            fontWeight: 500,
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {isActive ? "Activate" : "Deactivate"}
                                    </Typography>
                                </Stack>
                                <CustomizedSwitches
                                    checked={isActive}
                                    onChange={handleStatusToggle}
                                    color={isActive ? "success" : "error"}
                                />
                            </Stack>
                        </MenuItem>
                    </>
                )}
            </Menu>
        </Box>
    );
};

// Predefined action types for common use cases
export const ActionTypes = {
    VIEW: {
        label: "View",
        icon: <VisibilityIcon fontSize="small" />,
        color: "#0288d1",
        hoverColor: "#e3f2fd",
    },
    EDIT: {
        label: "Update",
        icon: <EditIcon fontSize="small" />,
        color: "#1976d2",
        hoverColor: "#e3f2fd",
    },
    DELETE: {
        label: "Delete",
        icon: <DeleteIcon fontSize="small" />,
        color: "#d32f2f",
        hoverColor: "#ffebee",
    },
    TRACKING: {
        label: "View Live Tracking",
        icon: <LocationOnIcon fontSize="small" />,
        color: "#4caf50",
        hoverColor: "#e8f5e8",
    },
    WHATSAPP_RESEND: {
        label: "Resend WhatsApp",
        icon: <MessageIcon fontSize="small" />,
        color: "#25d366",
        hoverColor: "#e8f5e8",
    },
};

export default ActionMenu;
